import classNames from 'classnames'
import { useState } from 'react'

import { useGetTeamRequestsQuery } from 'features/TeamChat/api/teamChat.api'
import type {
  TeamMember,
  TeamRequestFilter,
  TeamRequestsResponse,
} from 'features/TeamChat/api/teamChat.types'
import { TEAM_COPY } from 'features/TeamChat/teamChat.constants'
import styles from 'features/TeamChat/teamChat.module.scss'

import { TeamButton } from '../TeamButton'
import { PlusIcon } from '../TeamIcons'
import { EMPTY_REQUEST_DRAFT, useTeamDrafts } from '../TeamDrafts'
import { useFreshIds } from '../useFreshIds'

import { RequestCard } from './RequestCard'
import { RequestComposer } from './RequestComposer'
import { useRequestActions } from './useRequestActions'

type Props = { members: TeamMember[]; timeZone: string }
const FILTERS = [
  { value: 'active', label: TEAM_COPY.ACTIVE },
  { value: 'done', label: TEAM_COPY.DONE },
] as const satisfies ReadonlyArray<{ value: TeamRequestFilter; label: (n: number) => string }>

const EMPTY: TeamRequestsResponse['requests'] = []

type ListState = { data?: TeamRequestsResponse; isError: boolean }

// Nothing while loading, an error line on failure, the empty text only after a successful empty load.
const statusText = ({ data, isError }: ListState, empty: string): string | null => {
  if (!data) return isError ? TEAM_COPY.LOAD_FAILED : null
  return data.requests.length === 0 ? empty : null
}

export const RequestsTab = ({ members, timeZone }: Props) => {
  const [filter, setFilter] = useState<TeamRequestFilter>('active')
  const { request, setRequest } = useTeamDrafts()
  // Both responses carry both counts, so the Done list is fetched only while it is shown.
  const active = useGetTeamRequestsQuery('active')
  const done = useGetTeamRequestsQuery('done', { skip: filter !== 'done' })
  const { busyIds, run } = useRequestActions()
  const shown = filter === 'active' ? active : done
  const counts = (filter === 'done' && done.data) || active.data
  const requests = shown.data?.requests ?? []
  const freshActive = useFreshIds(active.data?.requests ?? EMPTY, Boolean(active.data))
  const isFresh = (id: number) => filter === 'active' && freshActive(id)
  const status = statusText(shown, filter === 'active' ? TEAM_COPY.NO_ACTIVE : TEAM_COPY.NO_DONE)

  return (
    <div className={styles.body}>
      <div className={styles.filters} role='group' aria-label={TEAM_COPY.FILTER_LABEL}>
        {FILTERS.map(({ value, label }) => (
          <button
            key={value}
            type='button'
            aria-pressed={filter === value}
            className={classNames(styles.filter, { [styles.filterOn]: filter === value })}
            onClick={() => setFilter(value)}>
            {label(value === 'active' ? counts?.active_count ?? 0 : counts?.done_count ?? 0)}
          </button>
        ))}
      </div>
      <div key={filter} className={classNames(styles.scroll, styles.fadeIn)}>
        {status && <div className={styles.empty}>{status}</div>}
        {requests.map((r) => (
          <RequestCard
            key={r.id}
            request={r}
            fresh={isFresh(r.id)}
            members={members}
            timeZone={timeZone}
            busy={busyIds.has(r.id)}
            onAction={run}
          />
        ))}
      </div>
      {request.open ? (
        <RequestComposer members={members} onClose={() => setRequest(EMPTY_REQUEST_DRAFT)} />
      ) : (
        <div className={styles.footer}>
          <TeamButton variant='outline' onClick={() => setRequest((s) => ({ ...s, open: true }))}>
            <PlusIcon />
            {TEAM_COPY.NEW_REQUEST}
          </TeamButton>
        </div>
      )}
    </div>
  )
}
